import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL } from "config/env";
import {
  isRefreshRetryAllowed,
  hasUsableRefreshToken,
  extractRotatedTokens,
} from "./refreshUtils.mjs";

export const UPDATE_FILED = "authUpdateField";
export const AUTH_SUCCESS = "authSuccess";
export const AUTH_REST = "authReset";
export const AUTH_INIT = "authINIT";
export const TOGGLE_DARK_MODE = "authToggleDarkMode";
export const TOGGLE_SIDE_BAR = "authToggleSideBar";
export const SET_DASHBOARD_DATA = "authDashboardData";
const ACCESS_TOKEN_STORAGE_KEY = "token";
const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
let refreshPromise = null;

export const updateFields = ({ prop, value }) => ({
  type: UPDATE_FILED,
  payload: { prop, value },
});

const updateAxiosAuthHeader = () => {
  const token = getToken();
  axiosInstance.defaults.headers.Authorization = token ? `Bearer ${token}` : "";
};

export function setToken(token, refreshToken = null) {
  if (typeof window !== "undefined") {
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
    if (typeof refreshToken === "string" && refreshToken.length > 0) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    }
    updateAxiosAuthHeader();
  }
}

export function getToken() {
  if (typeof window !== "undefined") {
    return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  }
  return null;
}

export function getRefreshToken() {
  if (typeof window !== "undefined") {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  }
  return null;
}

export function removeToken() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    updateAxiosAuthHeader();
    return null;
  }
  return null;
}

export function getSideBar() {
  if (typeof window !== "undefined") return localStorage.getItem("sidebar");
}

export const useAuth = () => {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  return {
    ...auth,
    getMe,
    getToken,
    submitLogin,
    dispatch,
    logout,
    submitInit,
    submitResetPassword,
    toggleDarkMode,
    toggleSideBar,
    getSideBar,
    handleSuccessMessages,
    getDashboardData,
  };
};
export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  //timeout: 5000,
  headers: {
    Authorization: getToken() ? `Bearer ${getToken()}` : "",
    "Accept-Language": "fa",
  },
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (typeof window === "undefined") {
      return Promise.reject(error);
    }

    const originalRequest = error?.config;
    const responseStatus = error?.response?.status;
    const requestUrl = originalRequest?.url || "";

    if (
      !isRefreshRetryAllowed({
        status: responseStatus,
        requestUrl,
        hasOriginalRequest: !!originalRequest,
        hasRetried: !!originalRequest?.__retriedWithRefresh,
      })
    ) {
      return Promise.reject(error);
    }

    const currentRefreshToken = getRefreshToken();
    if (!hasUsableRefreshToken(currentRefreshToken)) {
      removeToken();
      return Promise.reject(error);
    }

    try {
      if (!refreshPromise) {
        refreshPromise = axios.post(
          `${API_BASE_URL}/auth/refresh-token`,
          { refresh_token: currentRefreshToken },
          {
            headers: {
              "Accept-Language": "fa",
            },
          }
        );
      }

      const refreshResponse = await refreshPromise;
      const { token, refreshToken } = extractRotatedTokens(
        refreshResponse?.data
      );
      setToken(token, refreshToken);
      originalRequest.__retriedWithRefresh = true;
      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${token}`,
      };
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      removeToken();
      return Promise.reject(refreshError);
    } finally {
      refreshPromise = null;
    }
  }
);
export const submitInit = (data) => async (dispatch) => {
  try {
    const result = await axiosInstance.post("/auth/init", data);
    dispatch({
      type: AUTH_INIT,
      payload: {
        user_name: data.user_name,
        step: result.data.next,
        remember_token: result.data.remember_token,
      },
    });
  } catch (e) {
    handleErrors(e);
  }
};
export const toggleDarkMode = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: TOGGLE_DARK_MODE,
    });
  } catch (e) {
    handleErrors(e);
  }
};

export const toggleSideBar = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: TOGGLE_SIDE_BAR,
    });
  } catch (e) {
    handleErrors(e);
  }
};

export const submitLogin = (data, cb) => async (dispatch) => {
  try {
    const result = await axiosInstance.post("/auth/login", {
      session_to_be_remove: null,
      auto_remove_old_session: 1,
      ...data,
    });
    dispatch({
      type: AUTH_SUCCESS,
      payload: { userMeta: result.data.me },
    });
    setToken(result.data.token, result.data.refresh_token);
    cb();
  } catch (e) {
    handleErrors(e);
  }
};
export const submitCompelet = (data) => async (dispatch) => {
  try {
    const result = await axiosInstance.post("/auth/complate", data);
    dispatch({
      type: AUTH_SUCCESS,
      payload: { userMeta: result.data.me },
    });
    setToken(result.data.token, result.data.refresh_token);
  } catch (e) {
    handleErrors(e);
  }
};
export const submitResetPassword = (data, cb) => async (dispatch) => {
  try {
    const result = await axiosInstance.post("/auth/reset-password", data);

    dispatch({
      type: AUTH_SUCCESS,
      payload: { userMeta: result.data.me },
    });
    setToken(result.data.token, result.data.refresh_token);
    cb();
  } catch (e) {
    handleErrors(e);
  }
};
export const getMe = () => async (dispatch) => {
  try {
    const result = await axiosInstance.get("/me");
    dispatch({
      type: AUTH_SUCCESS,
      payload: { userMeta: result.data },
    });
  } catch (e) {
    handleErrors(e);
  }
};
export const getDashboardData = (from = null, to = null) => async (
  dispatch
) => {
  try {
    dispatch(updateFields({ prop: "LoadDashboardData", value: true }));
    let url = "/panel/me/dashboard";

    if (from) {
      url = `/panel/me/dashboard?from=${from}`;
    }
    if (to) {
      url = `/panel/me/dashboard?to=${to}`;
    }
    if (to && from) {
      url = `/panel/me/dashboard?from=${from}&to=${to}`;
    }
    const result = await axiosInstance.get(url);
    dispatch({
      type: SET_DASHBOARD_DATA,
      payload: result.data,
    });
    dispatch(updateFields({ prop: "LoadDashboardData", value: false }));
  } catch (e) {
    handleErrors(e);
    dispatch(updateFields({ prop: "LoadDashboardData", value: false }));
    dispatch({
      type: SET_DASHBOARD_DATA,
      payload: null,
    });
  }
};
export const logout = () => async (dispatch) => {
  try {
    await axiosInstance.post("/auth/logout");
    dispatch({
      type: AUTH_REST,
    });
    removeToken();
  } catch (e) {
    dispatch({
      type: AUTH_REST,
    });
    removeToken();
  }
};

export const resetAuth = () => async (dispatch) => {
  try {
    dispatch({
      type: AUTH_REST,
    });
  } catch (e) {
    handleErrors(e);
  }
};

export const handleErrors = (e) => {
  if (e && !e.response) {
    toast.error("خطای شبکه لطفا مجددا تلاش کنید", {
      position: "bottom-left",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    return;
  }

  if (e.response.data.errors) {
    for (const [key, value] of Object.entries(e.response.data.errors)) {
      value.forEach((element) => {
        toast.error(element, {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
    }
  }
  if (e.response && e.response.status == 401) {
    toast.error("عدم احراز هویت", {
      position: "bottom-left",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

    removeToken();
    window.location = "/";
    return;
  }
  toast.error(e.response.data.message, {
    position: "bottom-left",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

export const handleSuccessMessages = (message) => {
  toast.success(message, {
    position: "bottom-left",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

export const handleDangerMessages = (message) => {
  toast.error(message, {
    position: "bottom-left",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};
